import { ExclamationCircleOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Grid, Input, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React from 'react';
import { LOAD_FAILED_TEXT } from 'pages/data-import/utils';
import { useDataIssues } from '../context';
import { IssueList } from '../use-issue-list';
import {
  EMPTY_GROUP_TEXT,
  LOADING_TEXT,
  NO_FILE_TEXT,
  PAGE_SIZE,
  formatCount,
  noMatchText,
} from '../utils';
import styles from '../style.module.scss';

/** Một cột của bảng; ở màn hẹp mỗi cột thành một dòng "tên cột: giá trị" trong cùng một ô. */
export interface IssueField<T> {
  key: string;
  title: string;
  /** Bề rộng cột ở màn rộng (px). Mọi cột đều phải có để chữ không bị bẻ giữa chừng. */
  width: number;
  align?: 'right';
  render: (row: T) => React.ReactNode;
}

interface Props<T> {
  list: IssueList<T>;
  fields: IssueField<T>[];
  rowKey: (row: T) => string;
  /** Một câu giải thích nhóm này gồm những ai. */
  note: React.ReactNode;
  /** Chữ mờ trong ô tìm: ngắn, vừa ô ở màn 360px. */
  searchPlaceholder: string;
  /** Tên đầy đủ của ô tìm cho trình đọc màn hình và khi rê chuột. */
  searchLabel: string;
  /**
   * Màn hẹp: cột làm dòng đầu của mỗi ô (mặc định là cột đầu tiên). Dòng đầu có kèm tên cột
   * khi giá trị tự nó không nói lên là gì.
   */
  compactHead?: { key: string; showTitle?: boolean };
  /** Dòng mở rộng (tab Đổi TikTok ID): các dòng có khoá nằm trong `keys` đang mở. */
  expanded?: { keys: string[]; render: (row: T) => React.ReactNode };
}

/**
 * Bảng dùng chung cho bốn tab: ô tìm, nút Tải lại, phân trang phía máy chủ và đủ các trạng thái
 * đang tải / trống / tìm không thấy / tải lỗi. Tải lỗi không bao giờ hiện giống "không có dữ liệu".
 * Màn rộng: bảng nhiều cột, cuộn ngang trong khung riêng nếu thiếu chỗ. Màn dưới 992px (điện
 * thoại, máy tính bảng dọc): gộp các cột thành một ô xếp dọc nên không phải cuộn ngang. Mốc 992px
 * chứ không phải 768px: từ 768px thanh bên (264px) hiện cố định, khung nội dung chỉ còn
 * 450–670px nên bảng 1.100px chỉ thấy được khoảng một nửa (đo 30/09 ở 800px: khung 480px).
 */
function IssueTable<T extends object>({
  list,
  fields,
  rowKey,
  note,
  searchPlaceholder,
  searchLabel,
  compactHead,
  expanded,
}: Props<T>) {
  const { hasData, reload } = useDataIssues();
  // Cùng mốc với bốn ô số (2 cột dưới 992px, style.module.scss).
  const compact = Grid.useBreakpoint().lg === false;
  const { items, total, page, keyword, loading, failed } = list;

  const reloadButton = (
    <Button icon={<ReloadOutlined />} loading={loading} onClick={reload}>
      Tải lại
    </Button>
  );

  const emptyText = failed ? (
    <div className={styles.failed} role="alert">
      <span className={styles.failedText}>
        <ExclamationCircleOutlined aria-hidden /> {LOAD_FAILED_TEXT}
      </span>
      <Button type="primary" icon={<ReloadOutlined />} onClick={reload}>
        Tải lại
      </Button>
    </div>
  ) : loading ? (
    LOADING_TEXT
  ) : keyword ? (
    noMatchText(keyword)
  ) : hasData === false ? (
    NO_FILE_TEXT
  ) : (
    EMPTY_GROUP_TEXT
  );

  const head = fields.find((field) => field.key === compactHead?.key) || fields[0];
  const rest = fields.filter((field) => field !== head);
  const columns: ColumnsType<T> = compact
    ? [
        {
          key: 'stack',
          render: (_, row) => (
            <div className={styles.stack}>
              <div className={styles.stackHead}>
                {compactHead?.showTitle && <div className={styles.sub}>{head.title}</div>}
                {head.render(row)}
              </div>
              <dl className={styles.stackList}>
                {rest.map((field) => (
                  <div className={styles.stackRow} key={field.key}>
                    <dt>{field.title}</dt>
                    <dd>{field.render(row)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ),
        },
      ]
    : fields.map((field, index) => ({
        key: field.key,
        title: field.title,
        width: field.width,
        align: field.align,
        // Ghim cột đầu: cuộn ngang vẫn biết đang xem dòng của ai.
        fixed: index === 0 ? ('left' as const) : undefined,
        render: (_: unknown, row: T) => field.render(row),
      }));

  const tableWidth = fields.reduce((sum, field) => sum + field.width, 0);
  const showCount = !failed && !loading && total > 0;

  return (
    <div>
      <p className={styles.note}>{note}</p>
      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder={searchPlaceholder}
          aria-label={searchLabel}
          title={searchLabel}
          maxLength={80}
          value={list.input}
          onChange={(event) => list.setInput(event.target.value)}
          onPressEnter={list.searchNow}
        />
        {reloadButton}
      </div>
      {showCount && (
        <p className={styles.count}>
          {keyword
            ? `${formatCount(total)} Creator khớp "${keyword}"`
            : `${formatCount(total)} Creator`}
        </p>
      )}
      {failed && items.length > 0 && (
        <Alert
          className={styles.section}
          type="warning"
          showIcon
          message="Chưa tải lại được. Bảng đang hiện kết quả của lần tải trước."
        />
      )}
      <div className="custom-table">
        <Table<T>
          size="small"
          rowKey={rowKey}
          loading={loading}
          dataSource={items}
          columns={columns}
          showHeader={!compact}
          scroll={compact ? undefined : { x: tableWidth }}
          locale={{ emptyText }}
          expandable={
            expanded
              ? {
                  expandedRowKeys: expanded.keys,
                  expandedRowRender: expanded.render,
                  showExpandColumn: false,
                }
              : undefined
          }
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            showLessItems: true,
            hideOnSinglePage: true,
            onChange: list.setPage,
          }}
        />
      </div>
    </div>
  );
}

export default IssueTable;
